"use client";

import NewHomePage from "@/components/index/sections/hero/NewHomePage";
import SelectedDogCard from "@/components/hlidaci-pes/SelectedDogCard";
import {
  WatchDogResultsHint,
  WatchDogTopHint,
} from "@/components/hlidaci-pes/watchdog-page/WatchDogPageHints";
import { WatchDogResultsSection } from "@/components/hlidaci-pes/watchdog-page/WatchDogResultsSection";
import { WatchDogSelectionPanelCard } from "@/components/hlidaci-pes/watchdog-page/WatchDogSelectionPanelCard";
import ModalWindows from "@/components/ModalWindows";
import { AppPageIntroSlot } from "@/components/layout/AppPageIntroSlot";
import { NewWatchdogDialog } from "@/components/modals/CreateNewWatchDog";
import { useAnalysisContext } from "@/context/AnalysisContext";
import { useAppContext } from "@/context/AppContext";
import { useMenuItemsContext } from "@/context/MenuItemsContext";
import { useWatchDogContext } from "@/context/WatchDogContext";
import { BackgroundOrbits } from "@/components/BackgroundOrbits";
import { useLanguage } from "@/context/LanguageContext";
import { useSessionContext } from "@/context/SessionContext";
import { useHintWindowPreference } from "@/hooks/useHintWindowPreference";

function formatTemplate(
  template: string,
  values: Record<string, string | number | null | undefined>,
) {
  return Object.entries(values).reduce(
    (result, [key, value]) => result.replace(`{${key}}`, String(value ?? "")),
    template,
  );
}

function getWatchDogCountLabel(
  watchDogCount: number,
  copy: {
    oneWatchdog: string;
    watchdogCountFew: string;
    watchdogCountMany: string;
  },
) {
  if (watchDogCount === 0 || watchDogCount > 4) {
    return formatTemplate(copy.watchdogCountMany, { count: watchDogCount });
  }

  if (watchDogCount === 1) {
    return copy.oneWatchdog;
  }

  return formatTemplate(copy.watchdogCountFew, { count: watchDogCount });
}

export default function WatchDogPageClient() {
  const { isLoggedIn } = useAppContext();
  const { isInitialLoading } = useSessionContext();

  if (isInitialLoading || !isLoggedIn) {
    return <NewHomePage heroVariant="watchdog" />;
  }

  return <AuthenticatedWatchDog />;
}

function AuthenticatedWatchDog() {
  const { language } = useLanguage();
  const copy = {
    cs: {
      oneWatchdog: "1 hlídací pes",
      watchdogCountFew: "{count} hlídací psi",
      watchdogCountMany: "{count} hlídacích psů",
      analyticsView: "Analytický přehled",
      compactView: "Kompaktní přehled",
      detailedView: "Detailní přehled",
      waitingForSelection: "Čeká na výběr",
      noWatchdogYet: "Zatím bez hlídacího psa",
      analyticsDescription:
        "Zobrazen je cenový vývoj, odhad nájmu i interaktivní detail vybraného hlídacího psa.",
      compactDescription:
        "Výsledky jsou zobrazené ve vizuálně úsporných kartách pro rychlé porovnání.",
      detailedDescription:
        "Vidíte podrobný seznam nabídek s důrazem na obsah a rychlé akce.",
      waitingDescription:
        "Vyberte konkrétního hlídacího psa a načtěte jeho výsledky.",
      emptyDescription:
        "Přidejte prvního hlídacího psa a stránka se okamžitě naplní přehledem i výsledky.",
      loadingMembership: "Načítám informace o členství.",
      limitReached:
        "V členství {membership} máte vyčerpán počet hlídacích psů.",
      canCreate: "Můžete vytvořit až {count} {label}",
      emptyResults:
        "Zatím zde nejsou žádné výsledky, zkuste to později. První výsledky se objeví většinou do 5 minut.",
      noFavoriteResults:
        "Zatím zde nejsou žádné výsledky, uložte si nějaký inzerát do oblíbených.",
      noDateRangeResults: "Ve vybraném období nejsou žádné výsledky.",
    },
    en: {
      oneWatchdog: "1 watchdog",
      watchdogCountFew: "{count} watchdogs",
      watchdogCountMany: "{count} watchdogs",
      analyticsView: "Analytics view",
      compactView: "Compact view",
      detailedView: "Detailed view",
      waitingForSelection: "Waiting for selection",
      noWatchdogYet: "No watchdog yet",
      analyticsDescription:
        "Price development, rent estimate, and interactive detail for the selected watchdog are displayed.",
      compactDescription:
        "Results are shown in visually compact cards for quick comparison.",
      detailedDescription:
        "You see a detailed list of offers with emphasis on content and quick actions.",
      waitingDescription: "Select a specific watchdog and load its results.",
      emptyDescription:
        "Add the first watchdog and the page will immediately fill with overview and results.",
      loadingMembership: "Loading membership information.",
      limitReached:
        "Your {membership} membership has reached its watchdog limit.",
      canCreate: "You can create up to {count} watchdogs",
      emptyResults:
        "There are no results here yet. Try again later. The first results usually appear within 5 minutes.",
      noFavoriteResults:
        "There are no results here yet, save some offer to your favorites.",
      noDateRangeResults: "There are no results in the selected period.",
    },
  }[language];
  const {
    userInfoGlobal,
    selectedDog,
    setSelectedDog,
    isLoadingWatchDogs,
    isLoadingResults,
    isLoadingAllResults,
    isCompactView,
    estateOffers,
    sortedEstateOffers,
    membershipInfo,
    watchdogs,
    isDisabled,
    isFavoriteView,
    hasSelectedDogResults,
    hasDateRangeResults,
  } = useWatchDogContext();

  const { isGraphView } = useAnalysisContext();
  const { PROFILE_ITEMS } = useAppContext();
  const {
    LOGGED_IN_ITEMS,
    setIsOpen,
    handleDiscardChanges,
    selectedItem,
    setSelectedItem,
  } = useMenuItemsContext();

  const { isOpen: isTopHintOpen, toggle: toggleTopHint } =
    useHintWindowPreference("watchdogOverview");
  const { isOpen: isResultsHintOpen, toggle: toggleResultsHint } =
    useHintWindowPreference("watchdogResults");
  const { isOpen: isPriceHintOpen, toggle: togglePriceHint } =
    useHintWindowPreference("watchdogPriceDevelopment");
  const watchDogList = watchdogs ?? [];
  const watchDogCount = watchDogList.length;
  const hasWatchDog = !isLoadingWatchDogs && watchDogCount > 0;
  const hasResults = sortedEstateOffers.length > 0;
  const isMembershipLoading = membershipInfo == null && isLoadingWatchDogs;
  const activeViewLabel = selectedDog
    ? isGraphView
      ? copy.analyticsView
      : isCompactView
        ? copy.compactView
        : copy.detailedView
    : hasWatchDog
      ? copy.waitingForSelection
      : copy.noWatchdogYet;
  const activeViewDescription = selectedDog
    ? isGraphView
      ? copy.analyticsDescription
      : isCompactView
        ? copy.compactDescription
        : copy.detailedDescription
    : hasWatchDog
      ? copy.waitingDescription
      : copy.emptyDescription;
  const addWatchDogTooltip = isMembershipLoading
    ? copy.loadingMembership
    : isDisabled
      ? formatTemplate(copy.limitReached, {
          membership: userInfoGlobal?.membership,
        })
      : formatTemplate(copy.canCreate, {
          count: membershipInfo?.maxWatchDogs,
          label: membershipInfo?.maxWatchDogsString,
        });
  const isResultsLoading =
    hasWatchDog && !hasResults && (isLoadingResults || isLoadingAllResults);
  const showResultsEmptyState =
    hasWatchDog && !hasResults && !isLoadingResults && !isLoadingAllResults;

  const handleAddWatchDogClick = () => {
    if (isMembershipLoading) {
      return;
    }
    if (selectedItem != LOGGED_IN_ITEMS[0]) {
      handleDiscardChanges();
    }
    setSelectedItem(isDisabled ? PROFILE_ITEMS[0] : LOGGED_IN_ITEMS[0]);
    setIsOpen(true);
  };

  return (
    <section className="app-page-section overflow-hidden">
      <BackgroundOrbits />
      <div className="app-page-container flex flex-col gap-4">
        <AppPageIntroSlot
          hasWatchdog={hasWatchDog}
          hasWatchdogResults={hasResults}
        />
        <WatchDogTopHint
          watchDogCount={watchDogCount}
          activeViewLabel={activeViewLabel}
          activeViewDescription={activeViewDescription}
          isTopHintOpen={isTopHintOpen}
          onToggleTopHint={toggleTopHint}
        />

        <div data-tour="watchdog-selection">
          <WatchDogSelectionPanelCard
            watchdogs={watchDogList}
            watchDogCountLabel={getWatchDogCountLabel(watchDogCount, copy)}
            isLoading={isLoadingWatchDogs}
            selectedWatchDogId={selectedDog?.id ?? null}
            onSelectWatchDog={setSelectedDog}
            isMembershipLoading={isMembershipLoading}
            tooltip={addWatchDogTooltip}
            onAddWatchDogClick={handleAddWatchDogClick}
            summaryOffers={estateOffers ?? []}
            isSummaryLoading={isLoadingResults}
            emptyContent={
              watchDogCount === 0 && !isMembershipLoading && !isDisabled ? (
                <div className="mx-auto w-full max-w-[425px]">
                  <NewWatchdogDialog />
                </div>
              ) : undefined
            }
          />
        </div>

        {selectedDog ? (
          <div data-tour="watchdog-selected">
            <SelectedDogCard />
          </div>
        ) : null}

        <WatchDogResultsHint
          isGraphView={isGraphView}
          hasSelectedDog={!!selectedDog}
          isResultsHintOpen={isGraphView ? isPriceHintOpen : isResultsHintOpen}
          onToggleResultsHint={
            isGraphView ? togglePriceHint : toggleResultsHint
          }
        />

        {selectedDog ? (
          <div data-tour="watchdog-results">
            <WatchDogResultsSection
              hasSelectedDog
              hasResults={hasResults}
              isGraphView={isGraphView}
              isCompactView={isCompactView}
              isResultsLoading={isResultsLoading}
              showResultsEmptyState={showResultsEmptyState}
              emptyStateText={
                hasSelectedDogResults && !hasDateRangeResults
                  ? copy.noDateRangeResults
                  : isFavoriteView && hasDateRangeResults
                    ? copy.noFavoriteResults
                    : copy.emptyResults
              }
            />
          </div>
        ) : null}
        <ModalWindows />
      </div>
    </section>
  );
}
